import { useState } from 'react';
import {
    Button, DialogShell, IconPlus, IconUser, errorClass, fieldClass, labelClass,
} from '@timely/ui';
import {
    CONTACT_ROLE_SUGGESTIONS, MAX_CONTACTS, contactProblems, dialableNumber, emptyContact,
    type ContactPerson, type Customer,
} from '@timely/domain';
import { FormNotice } from '../screens/AuthLayout';
import { saveContacts } from './useBook';

/**
 * The other people to reach on an account (0033).
 *
 * The sheet brings one contact; collectors ring more — the accounts clerk, the
 * owner, whoever signs cheques. These are the company's own record, kept in
 * the product and never touched by a sync. Anyone who may edit the account can
 * change the list; everyone who may see the account can read it, and whoever
 * may work it rings and writes from it (`canContact`). Each person needs a phone
 * number or an email — a name alone reaches nobody — and the database refuses
 * anything else.
 */
export function ContactPeople({ customer, workspaceId, updatedAt, canEdit, canContact, disabled, onSaved, onError }: {
    customer: Customer;
    workspaceId: string;
    updatedAt: string;
    canEdit: boolean;
    canContact: boolean;
    disabled: boolean;
    onSaved: (message: string) => Promise<void>;
    onError: (message: string) => void;
}) {
    const people = customer.additionalContacts ?? [];
    const [editing, setEditing] = useState(false);

    return (
        <div className="mt-3 pt-3 border-t border-separator">
            <div className="flex items-center justify-between gap-2 mb-1">
                <h4 className="text-secondary font-semibold text-label">Other people</h4>
                {canEdit && (
                    <Button size="sm" variant="quiet" disabled={disabled} onClick={() => setEditing(true)}>
                        {people.length === 0 ? <><IconPlus size={14} />Add a person</> : 'Edit'}
                    </Button>
                )}
            </div>
            {people.length === 0 ? (
                <p className="text-secondary text-label-3">
                    {canEdit ? 'Nobody else is listed. Add the accounts clerk, the owner, or whoever signs cheques.' : 'Nobody else is listed.'}
                </p>
            ) : (
                <ul className="divide-y divide-separator">
                    {people.map((person, index) => (
                        <li key={index} className="py-2 flex items-start gap-2.5">
                            <span className="text-label-3 mt-0.5 flex-none" aria-hidden="true"><IconUser size={14} /></span>
                            <div className="min-w-0 flex-1">
                                <div className="text-secondary font-semibold text-label truncate">
                                    {person.name || 'Unnamed contact'}
                                    {person.role && <span className="font-normal text-label-3"> · {person.role}</span>}
                                </div>
                                <div className="text-caption flex flex-wrap gap-x-3">
                                    {person.phone && (canContact ? (
                                        <a href={`tel:${dialableNumber(person.phone) ?? person.phone.replace(/[^\d+]/g, '')}`} className="text-accent-text font-semibold num hover:underline">
                                            {person.phone}
                                        </a>
                                    ) : <span className="text-label-2 num">{person.phone}</span>)}
                                    {person.email && (canContact ? (
                                        <a href={`mailto:${person.email}`} className="text-accent-text font-semibold hover:underline truncate">
                                            {person.email}
                                        </a>
                                    ) : <span className="text-label-2 truncate">{person.email}</span>)}
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {editing && (
                <ContactEditor
                    company={customer.company}
                    initial={people}
                    onClose={() => setEditing(false)}
                    onSave={async list => {
                        await saveContacts(workspaceId, customer.id, list, updatedAt);
                        setEditing(false);
                        await onSaved(list.length === 0 ? 'Other people removed.' : 'Other people saved.');
                    }}
                    onError={onError}
                />
            )}
        </div>
    );
}

function ContactEditor({ company, initial, onClose, onSave, onError }: {
    company: string;
    initial: readonly ContactPerson[];
    onClose: () => void;
    onSave: (list: ContactPerson[]) => Promise<void>;
    onError: (message: string) => void;
}) {
    const [list, setList] = useState<ContactPerson[]>(() => (initial.length > 0 ? initial.map(p => ({ ...p })) : [emptyContact()]));
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    // Rows left completely empty are dropped rather than refused.
    const filled = list.filter(p => p.name.trim() || p.phone.trim() || p.email.trim() || p.role.trim());
    const problems = contactProblems(filled);
    const problemFor = (index: number, field: keyof ContactPerson) => {
        if (!touched) return undefined;
        const at = filled.indexOf(list[index]!);
        return at < 0 ? undefined : problems.find(p => p.index === at && p.field === field)?.message;
    };
    const set = (index: number, changes: Partial<ContactPerson>) =>
        setList(current => current.map((p, i) => (i === index ? { ...p, ...changes } : p)));

    const save = async () => {
        setTouched(true);
        if (problems.length > 0 || saving) return;
        setSaving(true);
        setProblem(null);
        try {
            await onSave(filled);
        } catch (failed) {
            const message = failed instanceof Error ? failed.message : 'That did not save.';
            setProblem(message);
            onError(message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogShell
            title="Other people on this account"
            subtitle={company}
            size="lg"
            onClose={onClose}
            closeOnEscape={!saving}
            onSubmit={e => { e.preventDefault(); void save(); }}
            footerNote={`${filled.length} of at most ${MAX_CONTACTS}`}
            footer={
                <>
                    <Button type="button" variant="quiet" disabled={saving} onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="primary" busy={saving}>{saving ? 'Saving…' : 'Save people'}</Button>
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}
            <p className="text-secondary text-label-3 mb-4">
                Kept in Timely Payment and never overwritten by a sync. Everyone who can see this account can read these numbers.
            </p>
            <datalist id="contact-roles">
                {CONTACT_ROLE_SUGGESTIONS.map(role => <option key={role} value={role} />)}
            </datalist>
            <ol className="space-y-4">
                {list.map((person, index) => (
                    <li key={index} className="rounded-control border border-separator p-3">
                        <fieldset>
                            <legend className="sr-only">Person {index + 1}</legend>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <Input id={`contact-${index}-name`} label="Name" value={person.name}
                                    onChange={v => set(index, { name: v })} error={problemFor(index, 'name')} />
                                <Input id={`contact-${index}-role`} label="What they do" value={person.role} list="contact-roles"
                                    onChange={v => set(index, { role: v })} error={problemFor(index, 'role')} />
                                <Input id={`contact-${index}-phone`} label="Phone" value={person.phone} inputMode="tel"
                                    onChange={v => set(index, { phone: v })} error={problemFor(index, 'phone')} />
                                <Input id={`contact-${index}-email`} label="Email" value={person.email} inputMode="email"
                                    onChange={v => set(index, { email: v })} error={problemFor(index, 'email')} />
                            </div>
                            <div className="mt-2 text-right">
                                <Button type="button" size="sm" variant="quiet"
                                    onClick={() => setList(current => current.filter((_, i) => i !== index))}>
                                    Remove {person.name.trim() || 'this person'}
                                </Button>
                            </div>
                        </fieldset>
                    </li>
                ))}
            </ol>
            {list.length < MAX_CONTACTS && (
                <Button type="button" variant="secondary" size="sm" className="mt-4" onClick={() => setList(current => [...current, emptyContact()])}>
                    <IconPlus size={14} />Add another person
                </Button>
            )}
            {touched && problems.some(p => p.field === 'list') && (
                <p className={errorClass} role="alert">{problems.find(p => p.field === 'list')!.message}</p>
            )}
        </DialogShell>
    );
}

function Input({ id, label, value, onChange, error, inputMode, list }: {
    id: string; label: string; value: string; onChange: (value: string) => void;
    error?: string | undefined; inputMode?: 'tel' | 'email'; list?: string;
}) {
    return (
        <div>
            <label htmlFor={id} className={labelClass}>{label}</label>
            <input
                id={id}
                value={value}
                {...(inputMode ? { inputMode } : {})}
                {...(list ? { list } : {})}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
                onChange={e => onChange(e.target.value)}
                className={fieldClass({ invalid: Boolean(error), className: 'w-full' })}
            />
            {error && <p id={`${id}-error`} className={errorClass} role="alert">{error}</p>}
        </div>
    );
}
